// Set of helper functions to facilitate wallet setup

import { BASE_BSC_SCAN_URLS, BASE_URL } from 'config'
import { ChainId } from '@pancakeswap/sdk'
import { VERSION } from 'config/constants/tokens'
import { nodes } from './getRpcUrl'

/**
 * Prompt the user to add BSC as a network on Metamask, or switch to BSC if the wallet is on a different network
 * @returns {boolean} true if the setup succeeded, false otherwise
 */
export const setupNetwork = async () => {
  const provider = window.ethereum
  if (provider) {
    const chainId = parseInt(process.env.REACT_APP_CHAIN_ID, 10)
    const chainIdHex = `0x${chainId.toString(16)}`
    
    // Check current chainId first to avoid unnecessary requests
    try {
      const currentChainId = (await provider.request({ method: 'eth_chainId' }) as unknown) as string
      if (currentChainId === chainIdHex) {
        // Already on the correct network
        return true
      }
    } catch (error) {
      // Ignore error and continue
      console.debug('Failed to get current chainId:', error)
    }
    
    // Network configuration based on chainId
    const networkConfig = {
      [ChainId.MAINNET]: {
        chainName: 'Binance Smart Chain',
        nativeCurrency: {
          name: 'BNB',
          symbol: 'BNB', // Use uppercase BNB (standard format)
          decimals: 18,
        },
        blockExplorerUrls: [BASE_BSC_SCAN_URLS[ChainId.MAINNET]],
      },
      [ChainId.TESTNET]: {
        chainName: 'Binance Smart Chain Testnet',
        nativeCurrency: {
          name: 'BNB',
          symbol: 'BNB', // Use uppercase BNB (standard format)
          decimals: 18,
        },
        blockExplorerUrls: [BASE_BSC_SCAN_URLS[ChainId.TESTNET]],
      },
    }
    
    const config = networkConfig[chainId] || networkConfig[ChainId.MAINNET]
    
    try {
      // First, try to switch to the network if it already exists
      try {
        await provider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: chainIdHex }],
        })
        return true
      } catch (switchError: any) {
        // Error code 4902 indicates that the chain has not been added to MetaMask
        if (switchError.code === 4902) {
          // Network doesn't exist, try to add it
          try {
            await provider.request({
              method: 'wallet_addEthereumChain',
              params: [
                {
                  chainId: chainIdHex,
                  chainName: config.chainName,
                  nativeCurrency: config.nativeCurrency,
                  rpcUrls: nodes,
                  blockExplorerUrls: config.blockExplorerUrls,
                },
              ],
            })
            return true
          } catch (addError: any) {
            // If adding fails due to symbol mismatch, network already exists
            // Return true as the network is available (even with different config)
            if (addError?.message?.includes('nativeCurrency.symbol')) {
              console.warn('Network already exists with different configuration')
              return true
            }
            console.error('Failed to add network:', addError)
            return false
          }
        } else if (switchError.code === 4001) {
          // User rejected the request
          console.error('User rejected the network switch request')
          return false
        } else {
          // Other errors (like symbol mismatch) - network exists but config differs
          // Return true as the network is available, just not with our exact config
          console.warn('Network switch failed, but network may still be available:', switchError.message)
          return true
        }
      }
    } catch (error: any) {
      console.error('Failed to setup the network in Metamask:', error)
      // If error is about symbol mismatch, network exists but with different config
      if (error?.message?.includes('nativeCurrency.symbol')) {
        console.warn('Network already exists with different configuration')
        return true
      }
      return false
    }
  } else {
    console.error("Can't setup the BSC network on metamask because window.ethereum is undefined")
    return false
  }
}

/**
 * Prompt the user to add a custom token to metamask
 * @param tokenAddress
 * @param tokenSymbol
 * @param tokenDecimals
 * @returns {boolean} true if the token has been added, false otherwise
 */
export const registerToken = async (tokenAddress: string, tokenSymbol: string, tokenDecimals: number) => {
  const tokenAdded = await window.ethereum.request({
    method: 'wallet_watchAsset',
    params: {
      type: 'ERC20',
      options: {
        address: tokenAddress,
        symbol: tokenSymbol,
        decimals: tokenDecimals,
        image: `${BASE_URL}/images/tokens/${tokenAddress}.svg?ver=${VERSION}`,
      },
    },
  })

  return tokenAdded
}

export const canRegisterToken = () =>
  typeof window !== 'undefined' &&
  // @ts-ignore
  !window?.ethereum?.isSafePal &&
  (window?.ethereum?.isMetaMask ||
    window?.ethereum?.isTrust ||
    window?.ethereum?.isCoinbaseWallet ||
    window?.ethereum?.isTokenPocket)
